import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Applies a code without changing server-side eligibility rules.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    if (this.#activeFetch) return;
    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;
    discountCode.value = discountCodeValue;
    const normalize = (code) => code.toLowerCase();

    const abortController = this.#createAbortController();
    this.#setPending(true);
    const timeout = setTimeout(() => abortController.abort('timeout'), 15000);

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => normalize(code) === normalize(discountCodeValue))) return;

      discountCode.removeAttribute('aria-invalid');
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      this.querySelector('[data-discount-request-error]')?.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Cart discount request failed');
      const data = await response.json();

      const appliedDiscount = data.discount_codes?.find(
        (discount) => normalize(discount.code) === normalize(discountCodeValue)
      );
      if (!appliedDiscount) throw new Error('Cart did not confirm the discount');
      if (appliedDiscount.applicable === false) {
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') throw new Error('Cart section unavailable');
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return normalize(discount.code) === normalize(discountCodeValue) && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, newHtml);
      this.querySelector('input[name="discount"]')?.focus({ preventScroll: true });
    } catch (error) {
      if (!abortController.signal.aborted || abortController.signal.reason === 'timeout') this.#handleDiscountError('request');
    } finally {
      clearTimeout(timeout);
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
        this.#setPending(false);
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    if (this.#activeFetch) return;
    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    this.#setPending(true);
    const timeout = setTimeout(() => abortController.abort('timeout'), 15000);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Cart discount request failed');
      const data = await response.json();

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') throw new Error('Cart section unavailable');
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, newHtml);
      this.querySelector('input[name="discount"]')?.focus({ preventScroll: true });
    } catch (error) {
      if (!abortController.signal.aborted || abortController.signal.reason === 'timeout') this.#handleDiscountError('request');
    } finally {
      clearTimeout(timeout);
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
        this.#setPending(false);
      }
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'request'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const requestError = this.querySelector('[data-discount-request-error]');
    const target = type === 'shipping' ? cartDiscountErrorShipping : type === 'request' ? requestError : cartDiscountErrorDiscountCode;
    requestError?.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    cartDiscountError.classList.remove('hidden');
    target?.classList.remove('hidden');
    const input = this.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement && type !== 'shipping') {
      if (type === 'discount_code') input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      input.focus({ preventScroll: true });
    }
  }

  /** Show progress and prevent duplicate submissions while Shopify responds. */
  #setPending(pending) {
    this.setAttribute('aria-busy', String(pending));
    const input = this.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement) input.readOnly = pending;
    this.querySelector('[data-discount-pending]')?.classList.toggle('hidden', !pending);
    for (const button of this.querySelectorAll('button')) {
      button.disabled = pending;
      if (button.type === 'submit') {
        if (pending) {
          button.dataset.idleLabel = button.textContent.trim();
          button.textContent = this.dataset.loadingLabel;
        } else if (button.dataset.idleLabel) {
          button.textContent = button.dataset.idleLabel;
          delete button.dataset.idleLabel;
        }
      }
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
